import React, { useEffect, useState } from "react";
import { api, fmtSigned, fmtOdds } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ProGate } from "@/components/ProGate";
import {
  Target, TrendingUp, TrendingDown, Minus, Activity, RefreshCw, LineChart as LineIcon,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine,
} from "recharts";
import { toast } from "sonner";

const fmtPct = (v, d = 2) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)}%`);
const fmtPts = (v, d = 1) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)} pts`);

export default function CLV() {
  return (
    <ProGate feature="CLV Report">
      <CLVInner />
    </ProGate>
  );
}

function CLVInner() {
  const [summary, setSummary] = useState(null);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/clv/summary"),
        api.get("/clv/bets"),
      ]);
      setSummary(s.data);
      setBets(b.data.bets);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post("/clv/recompute");
      toast.success("CLV recomputed");
      await load();
    } catch { toast.error("Recompute failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="clv-page">
      <PageHeader
        title="Closing Line Value"
        subtitle="The single best skill indicator in sports betting. Are you consistently beating the closing line?"
        actions={
          <button onClick={recompute} disabled={busy} className="iso-btn-outline" data-testid="clv-recompute-btn">
            {busy ? <RefreshCw className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            Recompute
          </button>
        }
      />

      {loading || !summary ? (
        <div className="iso-card p-12 text-center text-sm text-gray-500">Loading CLV data…</div>
      ) : summary.total_bets === 0 ? (
        <EmptyState />
      ) : (
        <>
          <KPIGrid s={summary} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <MarketBreakdown s={summary} />
            <BucketedWinRate s={summary} />
          </div>

          <BetLedger bets={bets} />
        </>
      )}
    </div>
  );
}

const EmptyState = () => (
  <div className="iso-card p-12 text-center" data-testid="clv-empty">
    <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
      <LineIcon className="size-5" />
    </div>
    <h3 className="mt-4 font-lexend">No bets to score yet</h3>
    <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
      Track a bet from the Simulator, Daily Picks, or Parlay Builder and we'll compare your
      locked-in price against the closing market — the real signal of whether you're sharp.
    </p>
  </div>
);

const KPIGrid = ({ s }) => {
  const lifetime = s.lifetime_clv_pct;
  const accent = lifetime > 0.25 ? "green" : lifetime < -0.25 ? "red" : "secondary";
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <KPI icon={Target} label="Lifetime CLV %" value={fmtPct(lifetime)} accent={accent}
        hint="Stake-weighted average across all bets" testid="kpi-lifetime-clv" />
      <KPI icon={TrendingUp} label="Beat the close" value={s.beat_close} accent="green"
        hint="Bets with CLV > +0.25%" testid="kpi-beat-close" />
      <KPI icon={Minus} label="Matched close" value={s.matched_close} accent="secondary"
        hint="Within ±0.25%" testid="kpi-matched-close" />
      <KPI icon={TrendingDown} label="Below close" value={s.below_close} accent="red"
        hint="Got a worse number than the close" testid="kpi-below-close" />
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, accent, hint, testid }) => {
  const bg = {
    primary: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-white",
    green: "bg-green text-white",
    red: "bg-red text-white",
  }[accent];
  return (
    <div className="iso-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="iso-label !mb-0">{label}</div>
        <div className={`flex size-8 items-center justify-center rounded-lg ${bg}`}>
          <Icon className="size-4" />
        </div>
      </div>
      <div className="mt-2 font-lexend text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-gray-500">{hint}</div>}
    </div>
  );
};

const MarketBreakdown = ({ s }) => {
  const rows = [
    { k: "moneyline", label: "Moneyline", ...s.markets.moneyline },
    { k: "spread",    label: "Spread",    ...s.markets.spread },
    { k: "total",     label: "Total",     ...s.markets.total },
    { k: "parlay",    label: "Parlay",    ...s.markets.parlay },
  ];
  return (
    <div className="iso-card lg:col-span-2" data-testid="clv-market-breakdown">
      <div className="border-b border-border px-6 py-4">
        <h3 className="font-lexend">Performance by market</h3>
        <p className="text-xs text-gray-500">Average CLV per market type. Positive % = beating the close on average.</p>
      </div>
      <div className="divide-y divide-border">
        {rows.map((r) => (
          <div key={r.k} className="flex items-center gap-4 px-6 py-3.5" data-testid={`clv-market-${r.k}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">{r.label}</div>
              <div className="text-[11px] uppercase tracking-wider text-gray-400">{r.count} bet{r.count === 1 ? "" : "s"}</div>
            </div>
            {r.avg_clv_pts != null && (
              <div className="text-right">
                <div className="iso-label !mb-0">Avg pts</div>
                <div className={`font-lexend text-sm font-bold ${r.avg_clv_pts > 0 ? "text-green-dark" : r.avg_clv_pts < 0 ? "text-red-dark" : "text-gray-500"}`}>
                  {fmtPts(r.avg_clv_pts, 2)}
                </div>
              </div>
            )}
            <div className="text-right w-28">
              <div className="iso-label !mb-0">Avg CLV</div>
              <div className={`font-lexend text-lg font-bold ${r.avg_clv_pct > 0.25 ? "text-green-dark" : r.avg_clv_pct < -0.25 ? "text-red-dark" : "text-gray-500"}`}>
                {fmtPct(r.avg_clv_pct)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BucketedWinRate = ({ s }) => {
  const rows = [
    { k: "positive", label: "Positive CLV", bucket: s.buckets.positive, accent: "text-green-dark", bar: "bg-green" },
    { k: "neutral",  label: "Neutral CLV",  bucket: s.buckets.neutral,  accent: "text-gray-600",   bar: "bg-gray-400" },
    { k: "negative", label: "Negative CLV", bucket: s.buckets.negative, accent: "text-red-dark",   bar: "bg-red" },
  ];
  return (
    <div className="iso-card" data-testid="clv-bucketed-winrate">
      <div className="border-b border-border px-6 py-4">
        <h3 className="font-lexend">Win rate by CLV</h3>
        <p className="text-xs text-gray-500">Long-term, positive-CLV bets should cluster above 50%.</p>
      </div>
      <div className="p-6 space-y-4">
        {rows.map((r) => {
          const b = r.bucket;
          const n = b.wins + b.losses;
          const wr = b.win_rate;
          return (
            <div key={r.k} data-testid={`clv-bucket-${r.k}`}>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs font-semibold text-gray-900">{r.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">
                    {n} settled · {b.push} push
                  </div>
                </div>
                <div className={`font-lexend text-lg font-bold ${r.accent}`}>
                  {wr == null ? "—" : `${wr}%`}
                </div>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-gray-100">
                <div className={`h-full rounded-full ${r.bar}`} style={{ width: `${Math.min(100, wr ?? 0)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BetLedger = ({ bets }) => {
  return (
    <div className="iso-card" data-testid="clv-bet-ledger">
      <div className="border-b border-border px-6 py-4">
        <h3 className="font-lexend">Bet ledger · CLV per play</h3>
        <p className="text-xs text-gray-500">Click a row to inspect the line-history timeline.</p>
      </div>
      <div className="divide-y divide-border">
        {bets.map((b) => (
          <BetRow key={b.id} bet={b} />
        ))}
      </div>
    </div>
  );
};

const BetRow = ({ bet }) => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const clv = bet.clv || {};
  const unavailable = !clv.clv_available;
  const pct = clv.clv_pct;
  const pts = clv.clv_pts;
  const positive = pct > 0.25;
  const negative = pct < -0.25;

  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && !history && bet.game_id && bet.market !== "parlay") {
      try {
        const r = await api.get(`/clv/line-history/${bet.game_id}`);
        setHistory(r.data);
      } catch { setHistory({ snapshots: [] }); }
    }
  };

  const chipClass = unavailable
    ? "iso-chip text-gray-400"
    : positive ? "iso-chip iso-chip-green"
    : negative ? "iso-chip iso-chip-red"
    : "iso-chip iso-chip-secondary";
  const ChipIcon = unavailable ? Minus : positive ? TrendingUp : negative ? TrendingDown : Minus;

  return (
    <div data-testid={`clv-bet-${bet.id}`}>
      <button onClick={toggle} className="flex w-full items-center gap-4 px-6 py-3 text-left hover:bg-gray-50">
        <div className="flex size-9 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
          <Activity className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900 truncate">{bet.game_label}</div>
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            {bet.market} · {bet.selection}
            {bet.line != null ? ` (${bet.line > 0 ? "+" : ""}${bet.line})` : ""} · {fmtOdds(bet.odds)}
          </div>
        </div>
        {pts != null && (
          <div className="text-right w-24 hidden md:block">
            <div className="iso-label !mb-0">Line move</div>
            <div className={`font-lexend text-xs font-bold ${pts > 0 ? "text-green-dark" : pts < 0 ? "text-red-dark" : "text-gray-500"}`}>
              {fmtPts(pts, 1)}
            </div>
          </div>
        )}
        <span className={chipClass} data-testid={`clv-chip-${bet.id}`}>
          <ChipIcon className="size-3" />
          {unavailable ? "No CLV" : fmtPct(pct)}
        </span>
      </button>

      {open && !unavailable && (
        <div className="bg-gray-50 px-6 py-4 border-t border-border">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 mb-4">
            <Stat label="Your odds" value={fmtOdds(bet.odds)} />
            <Stat label="Closing odds" value={clv.closing_odds != null ? fmtOdds(clv.closing_odds) : "—"} />
            {bet.line != null && <Stat label="Your line" value={fmtSigned(bet.line)} />}
            {clv.closing_line != null && <Stat label="Closing line" value={fmtSigned(clv.closing_line)} />}
          </div>
          <LineHistoryChart bet={bet} history={history} />
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="iso-label !mb-0">{label}</div>
    <div className="font-lexend text-sm font-bold text-gray-900">{value}</div>
  </div>
);

const LineHistoryChart = ({ bet, history }) => {
  if (!history) return <div className="text-xs text-gray-400 text-center py-6">Loading line history…</div>;
  const snaps = history.snapshots || [];
  if (snaps.length < 2) {
    return (
      <div className="text-xs text-gray-400 text-center py-6">
        Only {snaps.length} snapshot captured. Line history fills in as sync runs over time.
      </div>
    );
  }
  // Pick a series relevant to the market
  const key = bet.market === "moneyline"
    ? (bet.selection === "home" ? "moneyline_home" : "moneyline_away")
    : bet.market === "spread" ? "spread"
    : bet.market === "total" ? "total" : null;
  if (!key) return null;

  const data = snaps.map((s, i) => ({
    i,
    v: s.odds?.[key] ?? null,
    at: s.snapshot_at,
  })).filter((d) => d.v != null);

  if (data.length < 2) return null;

  const yourV = key === "moneyline_home" || key === "moneyline_away"
    ? bet.odds
    : bet.line;

  return (
    <div>
      <div className="iso-label mb-1">
        {bet.market === "moneyline" ? "Moneyline" : bet.market === "spread" ? "Home spread" : "Total"} movement
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data}>
          <XAxis dataKey="i" hide />
          <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} width={36} domain={["auto", "auto"]} />
          <Tooltip
            contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11 }}
            formatter={(v) => v}
            labelFormatter={(i) => `Snapshot ${Number(i) + 1}`}
          />
          {yourV != null && (
            <ReferenceLine y={yourV} stroke="#4e36f5" strokeDasharray="4 4" label={{ value: `Your: ${yourV}`, fill: "#4e36f5", fontSize: 10, position: "insideTopRight" }} />
          )}
          <Line type="monotone" dataKey="v" stroke="#111111" strokeWidth={2} dot={{ r: 2.5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
